// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input, NgZone,
  OnDestroy,
  OnInit,
  Renderer2, ViewChild
} from '@angular/core';
import * as urltemplate from 'url-template';

import { LMLoggerService } from '@services/lm-logger.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { ChangeDetectorRef } from '@angular/core';

import { LMBusyIndicatorService, LMContextService } from '@services/index';
import {
  LMListEventService,
  LMListGridPagingService,
  LMListSpecUtilService,
  LMListUtilService
} from './services/';
import { Observable, Subject, Subscription } from 'rxjs';
import { SohoBusyIndicatorDirective, SohoCalendarComponent } from 'ids-enterprise-ng';
import { AppConstants, LMConditionUtilService, LMTransformOptions } from '@utils/index';
import { LMColorId, LMThemeService } from '@banner/lm-theme.service';
import { LMListContentBaseComponent } from './lm-list-content-base.component';
import { LMFormatterService } from './formatters';

@Component({
  selector: 'lm-list-calendar',
  standalone: false,
  templateUrl: './lm-list-calendar.component.html',
  styleUrls: [ 'lm-list-calendar.component.scss' ]
})
export class LMListCalendarComponent extends LMListContentBaseComponent implements LMListContentInterface, AfterViewInit, OnDestroy, OnInit, AfterViewChecked {
  @ViewChild('SohoCalendarComponent') sohoCalendarComponent: SohoCalendarComponent;
  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective;

  public initialMonth = new Date(Date.now()).getMonth();
  public initialYear = new Date(Date.now()).getFullYear();
  public legends: Array<LMCalendarLegend> = [];
  public events = [];
  public eventTypes = [];
  public showViewChanger = true;
  public eventTooltip;
  public iconTooltip = 'status';
  public displayContextMenu = false;
  public uiLoadingIndicatorTimeout = 0;

  @HostBinding('class.calendar-container') get hostScrollable() {
    return true;
  }

  @HostBinding('class.card') showAsCard = false;

  /**
   * Used to retrieve data when this list is a 'dependentList'
   */
  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;
    this._fromDataViewChanged = true;
  }

  /**
   * ListState for a list and all it's contents
   */
  @Input() listState: LMListState;

  /**
   * The listSpec used to build
   * @type {LMListSpec}
   */
  @Input() set listSpec(listSpec: LMListSpec) {
    if (!listSpec) {
      return; // safety
    }

    this._listSpec = listSpec;

    if (this.lmListSpecUtilService.isMultiListPanelContent(this._listSpec)) {
      this.renderer.addClass(this.elementRef.nativeElement, 'multiListCalendar');
    } else {
      this.renderer.removeClass(this.elementRef.nativeElement, 'multiListCalendar');
    }

    // this.eventTooltip = this.getCalendar().mouseOverMessage !== undefined ? 'comments' : 'overflow';
    this._listSpecChanged = true;
  }

  get listSpec(): LMListSpec {
    return this._listSpec;
  }

  public type: string;
  private implementsCancelablePaging = false;
  private cancelPagingNotifier$: Subject<string>;
  private _fromDataViewChanged = false;
  private _listSpecChanged = false;
  private _monthChanged = false;
  private _filterCriteriaChanged = false;
  private _calendarViewChanged = false;
  private _chartName: string;

  /**
   * Internal member variable, Use listSpec getter when referencing this object.
   */
  private initialPageSubscription: Subscription;
  private node: Node;
  private renderMonthCallbackFunction: Function;
  private listComponentId: string;
  private _contextMenuEvent: any;
  private eventIndex: number;

  private static noMessage: SohoEmptyMessageOptions = {};

  /** public so it can be used in a template of any subclasses. */
  public emptyMessage: SohoEmptyMessageOptions = LMListCalendarComponent.noMessage;

  constructor(
    public elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmContextService: LMContextService,
    protected lmFormatUtilService: LMFormatUtilService,
    private lmFormatterService: LMFormatterService,
    private lmListEventService: LMListEventService,
    private lmListGridPagingService: LMListGridPagingService,
    private lmListSpecUtilService: LMListSpecUtilService,
    protected lmListUtilService: LMListUtilService,
    protected lmLoggerService: LMLoggerService,
    protected lmConditionUtilService: LMConditionUtilService,
    protected lmThemeService: LMThemeService,
    private lmUrlUtilService: LMUrlUtilService
  ) {
    super(lmLoggerService, lmThemeService, lmFormatUtilService, lmListUtilService, lmConditionUtilService);
    this.implementsCancelablePaging = this.lmContextService.isUiPagingRequestWaitTimeEnabled;
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
    this.lmBusyIndicatorService.showBusyIndicator();
  }

  ngOnInit(): void {
    // load initial month and year from listState
    if (this.listState?.month >= 0) {
      this.initialMonth = this.listState?.month;
    }

    if (this.listState?.year) {
      this.initialYear = this.listState?.year;
    }
  }

  ngAfterViewInit(): void {
    this._filterCriteriaChanged = false;
    // setTimeout(() => this.build());
  }

  ngAfterViewChecked() {
    let rebuild = false;

    if (this.listState.chartName != this._chartName) {
      this._chartName = this.listState.chartName;
      rebuild = true;
    }

    if (this._monthChanged) {
      this._monthChanged = false;
      rebuild = true;
    }

    if ((this._fromDataViewChanged) && this._listSpec) {
      this._fromDataViewChanged = false;
      rebuild = true;
    }

    if (this._filterCriteriaChanged) {
      this._filterCriteriaChanged = false;
      rebuild = true;
    }

    if (this._calendarViewChanged) {
      this.buildCalendarData(this.dataViewSet, this.renderMonthCallbackFunction);
      this._calendarViewChanged = false;
    }

    if (rebuild) {
      setTimeout(() => this.build());
    }

    this.listComponentId = this._listSpec.busListSpec.id + '_chart';
  }

  ngOnDestroy() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngOnDestroy' ]);
  }

  public onRenderMonthCallback = (node: Node, response: Function, args: any) => {
    this.renderMonthCallbackFunction = response;

    // onRenderMonthCallback gets called when changing from week/day to month view
    // need to rebuild if it is the same resource.
    if ((this._fromDataView && this._fromDataView.resourceId) === (this.listState && this.listState.fromResourceId)) {
      this._calendarViewChanged = true;
    }

    if (this.initialMonth !== args.month || this.initialYear !== args.year) {
      this._monthChanged = true;
    }

    // store off year and month in listState
    this.listState.month  = args.month;
    this.listState.year = args.year;
  }

  // public onCalendarDateSelectedCallback = (node: Node, args: SohoCalendarDateSelectedEvent) => {
  //     this.node = node;
  //     console.log('onCalendarEventSelectedCallback', args);
  // }

  public onCalendarEventClicked(e: SohoCalendarEventClickEvent) {
    if (!e || !e.event) {
      // if the event is not a cell click event
      return;
    }

    this.eventIndex = Number(e.event.id);
    const listRowClickedEvent: LMListRowClickedEvent = {
      originalEvent: e.originalEvent,
      item: this.node,
      row: this.eventIndex,
      cell: -1
    };
    this.lmListEventService.listRowClickedEvent(listRowClickedEvent);
  }

  public onCalendarEventDblClicked(e: SohoCalendarEventClickEvent) {
    if (!e || !e.event) {
      // if the event is not a cell click event
      return;
    }

    this.eventIndex = Number(e.event.id);
    const listRowClickedEvent: LMListRowClickedEvent = {
      originalEvent: e.originalEvent,
      item: this.node,
      row: this.eventIndex,
      cell: -1
    };
    this.lmListEventService.listRowDoubleClickedEvent(listRowClickedEvent);
  }

  public onCalendarEventContextMenu(e: SohoCalendarEventClickEvent) {
    if (!e || !e.event) {
      // if the event is not a cell click event
      return;
    }

    this.eventIndex = Number(e.event.id);
    const listRowClickedEvent: LMListRowClickedEvent = {
      originalEvent: e.originalEvent,
      item: this.node,
      row: this.eventIndex,
      cell: -1
    };
    this.lmListEventService.contextMenuEvent(<LMContextMenuEvent>{
      ...listRowClickedEvent,
      type: 'calendar'
    });
  }

  public isModified(): boolean {
    return false;
  }

  public showLegend() {
    // TODO (VW): need to revisit legend implementation
    return this.getCalendar().legend && this.lmContextService.isDeveloper();
  }

  public legendStates(): LMChartState[] {
    return this.getCalendar().legendStates;
  }

  public animate() {
    return true;
  }

  public showTooltips() {
    return true;
  }

  public getDirtyRows(commitEdits: boolean = undefined): Array<LMDataView> {
    return [];
  }

  public getType() {
    return this.type;
  }

  public refresh(): Observable<any> {
    return new Observable<any>(observer => {
      this.build();
      observer.next(true);
      observer.complete();
    });
  }

  public getDataViewSet(): LMDataViewSet {
    return this.dataViewSet;
  }

  public setDirtyIndicator(row: number, cell: number, showDirty: boolean) {
    // TODO implement me.
  }

  public onContextMenuItemSelected(actionReference: LMBusActionReference, selectedDataView?: LMDataView): void {
    // TODO
  }

  public updateDataViewTransaction(key: number, status: string, message: string): void {
    // TODO implement me.
  }

  public updateDataView(key: number, dataView: LMDataView) {
    // not implemented
  }

  public getDataViewIndex(event: SohoCalendarEvent): number {
    return -1;
  }

  public getSelectedRow(): number {
    const selectedRows = this.getSelectedRows();

    if (selectedRows.length === 1) {
      return selectedRows[0];
    } else {
      return 0;
    }
  }

  public getSelectedRows(): Array<number> {
    if (this._listSpec.busListSpec.selection === 'disabled') {
      return [];
    }

    return [ this.eventIndex ];
  }

  public unSelectAllRows() {
    // not implemented
  }

  public setSelectedRows(rows: Array<number>, scrollIntoView?: boolean) {    // [1, 3, 5 ]
  }

  public activateRow(row: number, scrollIntoView?: boolean) {
    // not implemented
  }

  public activatedRow(): number {
    return -1;
  }

  public get contextMenuId(): string {
    return this.lmListSpecUtilService.getContextMenuId(this._listSpec);
  }

  onContextMenu(event: LMContextMenuEvent): void {
    if (this.lmContextService.casualMode()
            || this._listSpec.showAsMingleWidget) {
      // disallow right click when:
      // - in casual mode
      // - in mingle widget.
      return;
    }

    this.lmLoggerService.log([ this, this.instanceId, 'onContextMenu()', event ]);

    setTimeout(() => {
      this._contextMenuEvent = event.originalEvent;
      this.displayContextMenu = true;
    });
  }

  private getStateInfo(): LMListStateInfo {
    const stateInfo: LMListStateInfo = {
      dataViewSet: this.getDataViewSet(),
      selectedDataView: this.getDataView(this.getSelectedRow()),
      selectedRows: this.getSelectedRows(),
      isModified: this.isModified(),
      searchFormDisplayed: false,
      isUpdatable: this.lmListSpecUtilService.allowUpdate(this.listSpec) || this.lmListSpecUtilService.implementsInlineCreate(this.listSpec),
      inlineCreateEnabled: false,
      inlineCreateModified: false,
      createWithinEnabled: false,
      isRootComponent: true,
      fromDataView: this.fromDataView,
      specialActions: [],
      displayAs: this.listState.displayAs
    };

    return stateInfo;
  }

  public getDataView(row: number): LMDataView {
    const dataViewSet = this.getDataViewSet();
    if (!dataViewSet || dataViewSet.data.length <= 0) {
      return null;
    }

    return dataViewSet.data[row];
  }

  private initialize() {
    if (!this._listSpec) {
      return;
    }

    this.showAsCard = (!this._listSpec.showAsMingleWidget && !this.lmListSpecUtilService.isDashBoardContent(this._listSpec));
  }

  protected build() {
    if (!this._listSpec) {
      return;
    }
    this.handleCancellableRequest();
    const chartName = this.getChartName();
    const chart = this.getChart(this.listSpec, chartName);
    this.eventTooltip = chart.mouseOverMessage !== undefined ? 'comments' : 'overflow';
    if (this.lmListSpecUtilService.isCubeView(this._listSpec)
            && !(this.lmListSpecUtilService.isDashBoardContent(this._listSpec))) {
      // Data is available for non dashboard types. Build the calendar using the data.
      if (this.dataViewSet) {
        // Data is available, so build calendar data directly.
        this.buildCalendarData(this.dataViewSet);
      }
      return;
    }

    if (this._listSpec.dependentList && !this._fromDataView) {
      this.dataViewSet = undefined;
      this.buildCalendarData(this.dataViewSet);
      /**
       * emitting the dataLoaded event with a undefined dataView should
       * cause the lm-list to fully disable.
       * NOTE: Must be called from a timeout to ensure this component is available to the observer.
       */
      setTimeout(() => this.lmListEventService.dataLoadedEvent(null));
      return;
    }

    if (this._listSpec.dependentList && this._fromDataView) {
      // Cancel outstanding initial page request, since the selection on the parent list has changed.
      if (this.initialPageSubscription && !this.initialPageSubscription.closed) {
        this.initialPageSubscription.unsubscribe();
      }
    }

    // todo 1. deal with tree view
    // todo 2. deal with cube options - probably need to be in url somewhere set on the server

    let url: string = this.buildInitialPageUrl();

    // -------------------------------------------------------------
    // Get pageSize to pass into the paging service
    // -------------------------------------------------------------
    const pageSize = -1;
    const postContent = undefined;

    if (this.lmListSpecUtilService.isCubeView(this._listSpec)
            && (this.lmListSpecUtilService.isDashBoardContent(this._listSpec))) {
      let delimiter = (url.indexOf('?') > -1) ? '&' : '?';
      // Add dimension and calendar name for Dashboard panel cube views.
      if (this._listSpec.cubeOptions.dimensionName) {
        url = url + delimiter + 'dimensionName=' + this._listSpec.cubeOptions.dimensionName;
        delimiter = '&';
      }
      url = url + delimiter + 'dashboardView=true';
    }
    // -------------------------------------------------------------
    // request the first page of records and log how long it took
    // -------------------------------------------------------------
    this.lmLoggerService.time({ id: 'LMListcalendar.initialPage() ' + this.instanceId });

    // check to see if month and year have been saved to listState
    if (this.listState?.month >= 0) {
      this.initialMonth = this.listState.month;
    }

    if (this.listState?.year) {
      this.initialYear = this.listState.year;
    }

    const firstDayOfMonth: Date = new Date(this.initialYear, this.initialMonth, 1);
    let startDate: Date = new Date();
    startDate = this.previousMonthDate(firstDayOfMonth);
    let endDate: Date = new Date();
    endDate = this.nextMonthDate(firstDayOfMonth);
    const transFormOptions: LMTransformOptions = {
      isRange: true
    };

    const formatDateAttribs: any = {
      pattern:      Soho.Locale.calendar().dateFormat.short
    };
    const startDateFormatted = Soho.Locale.formatDate(startDate, formatDateAttribs);
    const endDateFormatted = Soho.Locale.formatDate(endDate, formatDateAttribs);

    const startDateString = this.lmFormatUtilService.toDataValue(startDateFormatted, AppConstants.YYYYMMDD, transFormOptions);
    const endDateString = this.lmFormatUtilService.toDataValue(endDateFormatted, AppConstants.YYYYMMDD, transFormOptions);
    const filterValue = startDateString + ',' + endDateString;

    const filterCriteria: LMFilterFieldCriteria = {
      operator: this.lmListUtilService.getLMOperator('in-range'),
      fieldId: chart.fromDate,
      label: chart.fromDate,
      value: filterValue,
      isRequired: true,
    };

    this.initialPageSubscription = this.lmListGridPagingService.filterPage(url, [ filterCriteria ], pageSize, postContent)
      .subscribe((listPagingResponse: LMListPagingResponse) => {
        this.lmLoggerService.timeEnd('LMListcalendar.initialPage() ' + this.instanceId, [ this, listPagingResponse.dataViewSet ]);

        if (this.handleCancelableResponse(listPagingResponse)) {
          return;
        }

        if (listPagingResponse.status === 'COMPLETED') {
          this.dataViewSet = listPagingResponse.dataViewSet;
          this.savedSearches = listPagingResponse.savedSearchDataViewSet;
          this.postLoadCalendar(listPagingResponse, this.renderMonthCallbackFunction);
        } else {
          this.handleError(listPagingResponse.details, listPagingResponse.statusText);
          if (this.lmListSpecUtilService.isCubeView(this._listSpec)
                        && !(this.lmListSpecUtilService.isDashBoardContent(this._listSpec))) {
            // Do not emit dataloaded event.
            return;
          }
          this.lmListEventService.dataLoadedEvent(listPagingResponse);
        }
      });
  }

  private buildInitialPageUrl(): string {
    let url: string;
    if (this._listSpec.dependentList) {
      if (this.listState.currentPageUrl
        && this._fromDataView.resourceId === this.listState.fromResourceId
        && this.lmUrlUtilService.allCsksMatchContext(this.listState.currentPageUrl)
      ) {
        // if the fromDataView hasn't changed and there is a currentPageUrl then load that
        // to keep our position in the dependent list.
        url = this.listState.currentPageUrl;
      } else {
        // else resolve template against the fromDataView's resourceId
        const compiledUrlTemplate = urltemplate.parse(this.listUrlInfo.initialDataUrl);
        url = compiledUrlTemplate.expand({ resourceId: this._fromDataView.resourceId });
      }
    } else if (this.listState.currentPageUrl
      && this.lmUrlUtilService.allCsksMatchContext(this.listState.currentPageUrl)
    ) {
      url = this.listState.currentPageUrl;
    } else {
      url = this.listUrlInfo.initialDataUrl;
    }

    return url;
  }

  private postLoadCalendar(listPagingResponse: LMListPagingResponse, fn: Function) {
    this.buildCalendarData(listPagingResponse.dataViewSet, fn);

    if (this.lmListSpecUtilService.isCubeView(this._listSpec)
            && !(this.lmListSpecUtilService.isDashBoardContent(this._listSpec))) {
      // Do not emit dataloaded event.
      return;
    }
    setTimeout(() => this.lmListEventService.dataLoadedEvent(listPagingResponse));
  }

  private buildCalendarData(dataViewSet: LMDataViewSet, fn?: Function) {
    if (!dataViewSet) {
      return null;
    }
    this.getEvents(dataViewSet);
    this.getEventTypes();

    if (dataViewSet.data.length === 0) {
      // this.sohoCalendarComponent.renderMonthCallback = null;
      this.sohoCalendarComponent.updated();
    } else {
      fn(this.events, this.eventTypes);
    }

    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  private evaluateEventIcon(dataView: LMDataView): LMCalendarAlert {
    let alert: LMCalendarAlert = null;

    if (this.getCalendar().alert) {
      if (this.getCalendar().redAlert && this.lmConditionUtilService.isValid([ this.getCalendar().redAlert.condition ], dataView)) {
        alert = {
          icon: 'icon-error',
          mouseoverText: this.getCalendar().redAlert.alertMouseOverMessage ? this.lmFormatUtilService.formatMessageRule(
            this.getCalendar().redAlert.alertMouseOverMessage, dataView) : ''
        };
      } else if (this.getCalendar().yellowAlert && this.lmConditionUtilService.isValid([ this.getCalendar().yellowAlert.condition ], dataView)) {
        alert = {
          icon: 'icon-alert',
          mouseoverText: this.getCalendar().yellowAlert.alertMouseOverMessage ? this.lmFormatUtilService.formatMessageRule(
            this.getCalendar().yellowAlert.alertMouseOverMessage, dataView) : ''
        };
      } else if (this.getCalendar().greenAlert && this.lmConditionUtilService.isValid([ this.getCalendar().greenAlert.condition ], dataView)) {
        alert = {
          icon: 'icon-info',
          mouseoverText: this.getCalendar().greenAlert.alertMouseOverMessage ? this.lmFormatUtilService.formatMessageRule(
            this.getCalendar().greenAlert.alertMouseOverMessage, dataView) : ''
        };
      } else if (this.getCalendar().blueAlert && this.lmConditionUtilService.isValid([ this.getCalendar().blueAlert.condition ], dataView)) {
        alert = {
          icon: 'icon-success',
          mouseoverText: this.getCalendar().blueAlert.alertMouseOverMessage ? this.lmFormatUtilService.formatMessageRule(
            this.getCalendar().blueAlert.alertMouseOverMessage, dataView) : ''
        };
      } else if (this.getCalendar().iconAlert && this.lmConditionUtilService.isValid([ this.getCalendar().iconAlert.condition ], dataView)) {
        alert = {
          icon: 'icon-' + this.getCalendar().iconAlert.icon,
          mouseoverText: this.getCalendar().iconAlert.alertMouseOverMessage ? this.lmFormatUtilService.formatMessageRule(
            this.getCalendar().iconAlert.alertMouseOverMessage, dataView) : ''
        };
      }
    }
    return alert;
  }

  private populateLegendStates(dataView: LMDataView) {
    if (this.getCalendar().legend) {
      const legendField = dataView.fields[this.getCalendar().legend];
      const legendFieldValue = legendField.value as string;
      const legendsLength = this.legends.length;
      if (legendsLength > 0) {
        const matchLegends = this.legends.filter(field => legendField.value === field.label);
        if (matchLegends.length === 0) {
          let color = null;
          switch (legendsLength) {
            case 1:
              color = LMColorId.amethyst;
              break;
            case 2:
              color = LMColorId.emerald;
              break;
            case 3:
              color = LMColorId.amber;
              break;
            case 4:
              color = LMColorId.alabaster;
              break;
            case 5:
              color = LMColorId.ruby;
              break;
            case 6:
              color = LMColorId.slate;
              break;
            case 7:
              color = LMColorId.turquoise;
              break;
            default:
              color = LMColorId.graphite;
          }
          this.legends.push({ color, label: legendFieldValue });
        }
      } else {
        this.legends.push({ color: LMColorId.azure, label: legendFieldValue });
      }
    }
  }

  private evaluateEventColor(subject: string) {
    if (this.legends.length > 0) {
      const matchLegends = this.legends.filter(field => subject === field.label.toString());

      if (matchLegends.length > 0) {
        return matchLegends[0].color;
      }
    }
    return LMColorId.amethyst;
  }

  private getEvents(dataViewSet: LMDataViewSet) {
    this.events = [];
    const chartName = this.getChartName();
    const chart = this.getChart(this.listSpec, chartName) as LMCalendarSpec;

    for (let s = 0; s < dataViewSet.data.length; s++) {
      const dataView = dataViewSet.data[s];
      this.populateLegendStates(dataView);
      const eventAlert = this.evaluateEventIcon(dataView);

      const fromDate = dataView.fields[chart.fromDate].value;
      const toDate = chart.toDate ?
        (dataView.fields[chart.toDate].value && dataView.fields[chart.toDate].value !== '00000000') ? dataView.fields[chart.toDate].value : null
        : null;
      const subject = chart.textMessage ? this.lmFormatUtilService.formatMessageRule(chart.textMessage, dataView) : '';
      const specFields = this.lmListUtilService.buildSpecFields(this.listSpec.busListSpec.displayFields);
      const mouseOver = chart.mouseOverMessage ? this.lmFormatUtilService.formatMessageRule(chart.mouseOverMessage, dataView, specFields) : '';
      const showDate = chart.dateField ? dataView.fields[chart.dateField].value : undefined;
      const event: LMCalendarEvent = {
        starts: this.lmFormatUtilService.yyyymmddhhmmssToDisplay(fromDate + '000000000'),
        ends: toDate ? this.lmFormatUtilService.yyyymmddhhmmssToDisplay(toDate + '235959999') : this.lmFormatUtilService.yyyymmddhhmmssToDisplay(fromDate + '235959999'),
        id: s.toString(),
        isAllDay: 'true',
        comments: mouseOver,
        subject,
        color: this.evaluateEventColor(subject),
        type: subject
      };

      if (eventAlert) {
        event.icon = eventAlert.icon;
        if (eventAlert.mouseoverText) {
          event.status = eventAlert.mouseoverText;
        }
      }
      this.events.push(event);
    }
  }

  private getEventTypes() {
    this.eventTypes = [];
    this.legends.forEach((legendItem: LMCalendarLegend) => {
      this.eventTypes.push(
        {
          id: legendItem.label,
          label: legendItem.label,
          color: legendItem.color,
          checked: true,
          click: null
        });
    });
  }

  public getLegendColorId(color: LMColorId) {
    return this.lmThemeService.getColorById(color).backgroundColorClass;
  }

  public addDataView(inlineCreateDataVie: LMDataView, row: number) {
    // not implemented
  }

  public removeDataView(row: number) {
    // not implemented
  }

  private getCalendar(): LMChartSpec {

    const chartName = this.getChartName();
    return this.listSpec.busListSpec.charts.find(chartSpec => chartSpec.id === chartName);
  }

  private getChartName(): string {

    if (this.listState.chartName) {
      return this.listState.chartName;
    } else if (this.listSpec.busListSpec.defaultChartName) {
      return this.listSpec.busListSpec.defaultChartName;
    } else {
      return this.listSpec.busListSpec.charts[0].id;
    }
    return null;
  }

  protected handleError(message: string, title: string) {
    const error: LMError = { message, title };
    this.setErrorLoadingMessage(error);
  }

  private noDaysInMonth(month: number, year: number): number {
    switch (month) {
      case 0:
      case 2:
      case 4:
      case 6:
      case 8:
      case 9:
      case 10:
      case 11:
        return 31;
      case 1: {
        if (year % 4 === 0) {
          return 29;
        } else {
          return 28;
        }
      }
      case 3:
      case 5:
      case 7:
        return 30;
    }
  }

  private previousMonthDate(date: Date): Date {
    if (date.getMonth() === 0) {
      return (new Date(date.getFullYear() - 1, 11, 1));
    } else {
      return (new Date(date.getFullYear(), date.getMonth() - 1, 1));
    }
  }

  private nextMonthDate(date: Date): Date {
    if (date.getMonth() === 11) {
      const endDate: number = this.noDaysInMonth(0, date.getFullYear());
      return (new Date(date.getFullYear() + 1, 0, endDate));
    } else {
      const endDate: number = this.noDaysInMonth(date.getMonth() + 1, date.getFullYear());
      return (new Date(date.getFullYear(), date.getMonth() + 1, endDate));
    }
  }

  private handleCancellableRequest(): void {
    if (this.implementsCancelablePaging && this.cancelPagingNotifier$) {
      this.cancelPagingNotifier$.next(AppConstants.THREAD_INTERRUPTED_BY_WEBUI);
      this.cancelPagingNotifier$.complete();
      this.cancelPagingNotifier$ = null;
    }
  }

  private handleCancelableResponse(response: LMListPagingResponse): boolean {
    if (this.implementsCancelablePaging) {
      if (response.status === 'STARTED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'started', cancelablePagingNotifier: response.cancelablePagingNotifier });
        this.cancelPagingNotifier$ = response.cancelablePagingNotifier;
        return true;
      } else if (response.status === 'WAITING') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'waiting' });
        return true;
      } else if (response.status === 'CANCELED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'canceled' });
        this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        return true;
      } else {
        this.cancelPagingNotifier$ = null;
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'finished' });
        return false;
      }
    } else {
      return false;
    }
  }

  protected loadManualData() {
    if (this.listSpec.dependentList && !this.componentDestroyed) {
      // Protect against a calendar list being defined with implements manual loading
      this.allowDataLoading = true;
      this.build();
    }
  }

  protected onFormButtonClicked(columnDef: ListGridColumn, dataView: LMDataView, row: any, cell: any, navigationParams: any, event: any): void {
    // Not implemented
  }

  protected fromDataViewChanged() {
    if (this.listSpec.dependentList && !this.componentDestroyed) {
      // Protect against a calendar list being defined with implements manual loading
      this.allowDataLoading = true;
      this.build();
    }
  }

  protected filterCriteriaChanged() {
    // See the actual processing of the changed filterCriteriaChanged is in ngAfterViewChecked
    this._filterCriteriaChanged = true;
    this.changeDetectorRef.markForCheck();
  }

  protected filterListCriteriaChanged(): void {
  }

  protected reportAsOfDateChanged(): void {
    //noop
  }

  public getChart(listSpec: LMListSpec, chartName: string): LMCalendarSpec | undefined {
    if (!listSpec?.busListSpec?.charts || !chartName) {
      return undefined;
    }
    return listSpec.busListSpec.charts.find(chartSpec => chartSpec.id === chartName);
  }
}
